import { ibowClient } from '@/init/ibowClient';
import { appConfig } from '../../app.config';
import { getCookie, setCookie } from './cookieHelper';
import {
  ApiError,
  CancelablePromise,
  common_BaseResponse,
} from '@/init/swagger';
import dayjs from '@/init/dayjs';

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || undefined;

export function apiRequest<T>({
  requestFunc,
  resolveFunc,
  rejectFunc,
  returnRawErrorData = false,
}: {
  requestFunc: () => CancelablePromise<T>;
  resolveFunc?: ((res: T) => T | void) | ((res: T) => Promise<T | void>);
  rejectFunc?: (err: unknown) => unknown;
  returnRawErrorData?: boolean;
}) {
  return requestFunc()
    .then((res) => {
      if (res && (res as common_BaseResponse).code == 999) {
        if (typeof window !== 'undefined') {
          const reloadCount = sessionStorage.getItem('reload-count');
          if (reloadCount) {
            sessionStorage.setItem(
              'reload-count',
              `${parseInt(reloadCount) + 1}`,
            );
          } else {
            sessionStorage.setItem('reload-count', '1');
          }
          location.reload();
        } else {
          sessionStorage.removeItem('reload-count');
        }
      }
      return resolveFunc ? resolveFunc(res) : res;
    })
    .catch(async (err) => {
      if (err && returnRawErrorData && rejectFunc) {
        rejectFunc({
          ...err,
        });
        return;
      }

      if (err instanceof ApiError) {
        console.error('ApiError', {
          ...err,
        });
      } else {
        console.error(err);
      }

      if (err && err.status == 401) {
        if (err.data?.auth_code == 1) {
          // error内容を確認してエラーを表示
        } else {
          return await refToken({ requestFunc, resolveFunc, rejectFunc });
        }
      }
      if (err && err.status == 429) {
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          if (url.searchParams.get('snackbar') !== 'tooManyRequests') {
            url.searchParams.set('snackbar', 'tooManyRequests');
            window.location.href = url.toString();
          }
          return;
        }
      }

      if (rejectFunc) {
        rejectFunc(err);
      }
    });
}

async function refToken<T>({
  requestFunc,
  resolveFunc,
  rejectFunc,
}: {
  requestFunc: () => Promise<T>;
  resolveFunc?: ((res: T) => T | void) | ((res: T) => Promise<T | void>);
  rejectFunc?: (err: unknown) => unknown;
}) {
  const refToken = await getCookie(appConfig.IBOW_REFRESH_TOKEN_COOKIE_NAME);
  return await ibowClient.authentication
    .postRefreshToken({ body: { ref_token: refToken } })
    .then(async (res) => {
      await setToken({ token: res.token, refToken: res.ref_token });
      return await requestFunc()
        .then((res) => {
          return resolveFunc ? resolveFunc(res) : res;
        })
        .catch((err) => {
          if (rejectFunc) {
            rejectFunc(err);
          }
        });
    })
    .catch(() => {
      // セッション切れのエラー表示
    });
}

export async function getToken() {
  return await getCookie(appConfig.IBOW_TOKEN_COOKIE_NAME);
}

export async function setToken({
  token,
  refToken,
}: {
  token: string;
  refToken: string;
}) {
  const time = dayjs().add(appConfig.CHECK_TOKEN_EXPIRE_TIME, 'minute');
  await setCookie(appConfig.IBOW_PREV_TOKEN_COOKIE_NAME, token, {
    domain: cookieDomain,
  });
  await setCookie(appConfig.IBOW_TOKEN_COOKIE_NAME, token, {
    domain: cookieDomain,
  });
  await setCookie(appConfig.IBOW_REFRESH_TOKEN_COOKIE_NAME, refToken, {
    domain: cookieDomain,
  });
  await setCookie(
    appConfig.IBOW_TOKEN_EXPIRE_TIME_COOKIE_NAME,
    time.toISOString(),
    {
      domain: cookieDomain,
    },
  );
}

export async function clearToken() {
  await setCookie(appConfig.IBOW_PREV_TOKEN_COOKIE_NAME, '', {
    domain: cookieDomain,
  });
  await setCookie(appConfig.IBOW_TOKEN_COOKIE_NAME, '', {
    domain: cookieDomain,
  });
  await setCookie(appConfig.IBOW_REFRESH_TOKEN_COOKIE_NAME, '', {
    domain: cookieDomain,
  });
  await setCookie(appConfig.IBOW_TOKEN_EXPIRE_TIME_COOKIE_NAME, '', {
    domain: cookieDomain,
  });
}
